import DragHandleIcon from "@mui/icons-material/DragHandle";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";

import { useRole } from "@4uhub/lib4uhub";
import { IDashboard } from "../../../models/dashboard";
import { DashBoardProvider } from "../../../store/contexts/DashBoardContext";
import { usePanel } from "../../../store/contexts/DashBoardPannelContext";
import ComponentsList from "./DashboardComponent/ComponentsList";
import DashboardMenu from "./DashboardMenu/DashboardMenu";
import DashBoardsEditAndReorderComponents from "./DashBoardsEditAndReorderComponents/DashBoardMenu";
import { Filters } from "./Filters/Filters";

interface IDimensions {
  width?: number;
  height?: number;
}

interface IDashBoardProps {
  data: IDashboard;
}

const DashBoard: React.FC<IDashBoardProps> = ({ data }) => {
  const { isView } = usePanel();

  const updatePermission = useRole(["Update"]);

  const deletePermission = useRole(["Delete"]);

  const ref = useRef<HTMLDivElement>(null);

  const [dimensions, setDimensions] = useState<IDimensions>({});

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = 0;
    }
  }, []);

  useLayoutEffect(() => {
    if (!ref.current) return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setDimensions({
          width: ref.current?.offsetWidth,
          height: ref.current?.offsetHeight,
        });
      }, 50); // ajuste o delay conforme necessário
    };

    const resizeObserver = new window.ResizeObserver(handleResize);

    resizeObserver.observe(ref.current);

    // Inicializa dimensões
    handleResize();

    return () => {
      resizeObserver.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <DashBoardProvider dashboard={data}>
      <Box
        ref={ref}
        sx={(t) => ({
          width: "100%",
          height: "100%",
          padding: t.spacing(2),
          backgroundColor: t.palette.grey[t.palette.mode === "dark" ? 900 : 50],
          borderRadius: t.shape.borderRadius / 10,
          overflow: "auto",
        })}
      >
        <Stack gap={2}>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            {!isView && updatePermission && (
              <IconButton
                className="myDraggable"
                size="small"
                sx={{
                  cursor: "grab",
                  "&:active": {
                    cursor: "grabbing",
                  },
                }}
              >
                <DragHandleIcon fontSize="small" />
              </IconButton>
            )}

            <Typography>{data.name}</Typography>

            <Stack direction={"row"} gap={2} alignItems={"center"}>
              {data.filter && <Filters filter={data.filter} />}
              {!isView && updatePermission && (
                <DashBoardsEditAndReorderComponents />
              )}
              {!isView && deletePermission && <DashboardMenu />}
            </Stack>
          </Stack>
          <ComponentsList height={dimensions.height} width={dimensions.width} />
        </Stack>
      </Box>
    </DashBoardProvider>
  );
};

export default memo(DashBoard);
